/* eslint-disable max-lines */
import { UnifiedAlarm } from '@hakimo-ui/hakimo/types';
import clsx from 'clsx';
import { useMemo, useRef, useState } from 'react';
import { alarmColor } from '../utils/alarmColor';
import {
  formatTime,
  getHoverTranslate,
  HOVER_MARGIN_TOP,
  TIMELINE_HEIGHT,
} from './utils';

interface Props {
  alarms: UnifiedAlarm[];
  onUpdatePlaybackSrc?: (params: string) => void;
  currentAlarmId?: string;
  timeZone: string;
  mediaType: 'video' | 'frames';
}

interface MediaWithAlarmData {
  id: string;
  url: string;
  severity: string;
  mediaType: 'video' | 'frames';
  timestamp?: number;
  startTimestamp?: number;
  endTimestamp?: number;
  isPastEvent?: boolean;
  isDedupEvent?: boolean;
  resolvedAlarm?: boolean;
}

export function CellMediaTimeline(props: Props) {
  const { alarms, timeZone, mediaType, onUpdatePlaybackSrc } = props;

  const timelineRef = useRef<HTMLDivElement>(null);
  const [hoverImagePosition, setHoverImagePosition] = useState<number | null>(
    null
  );
  const [hoverFrame, setHoverFrame] = useState<MediaWithAlarmData>();
  const [hoverTimePosition, setHoverTimePosition] = useState<number | null>(
    null
  );

  const isHoverEnabled = true;

  const { timelineStartTime, totalDuration } = useMemo(() => {
    let startTime: number | null = null;
    let endTime: number | null = null;
    alarms.forEach((alarm) => {
      if (!startTime || alarm.alarmStartTime < startTime) {
        startTime = alarm.alarmStartTime;
      }
      if (!endTime || alarm.alarmEndTime > endTime) {
        endTime = alarm.alarmEndTime;
      }
    });
    return {
      timelineStartTime: startTime ?? 0,
      // timelineEndTime: endTime ?? 0,
      totalDuration: (endTime ?? 0) - (startTime ?? 0),
    };
  }, [alarms]);

  // const tenMinutesAgo = new Date(new Date().getTime() - 10 * 60 * 1000);

  const getFramePosition = (frame: MediaWithAlarmData) => {
    let timeDiff = 0;
    if (frame.mediaType === 'frames') {
      timeDiff = (frame?.timestamp ?? 0) - timelineStartTime;
    } else {
      timeDiff =
        new Date(frame?.startTimestamp ?? 0).getTime() - timelineStartTime;
    }
    return (timeDiff / totalDuration) * 100;
  };

  const onHoverFrame =
    (frame: MediaWithAlarmData) =>
    (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      if (timelineRef.current) {
        const rect = timelineRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const percentage = (x / rect.width) * 100;
        setHoverImagePosition(percentage);
        setHoverFrame(frame);
      }
    };

  const onHoverTimeline = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (timelineRef.current) {
      const rect = timelineRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const percentage = (x / rect.width) * 100;
      setHoverTimePosition(percentage);
    }
  };

  const onFrameMouseLeave: React.MouseEventHandler<HTMLDivElement> = () =>
    setHoverImagePosition(null);

  const onTimelineMouseLeave: React.MouseEventHandler<HTMLDivElement> = () =>
    setHoverTimePosition(null);

  const getHoverTime = (position: number) => {
    if (position === null) return null;
    const hoverTime = new Date(
      timelineStartTime + (position / 100) * totalDuration
    );
    return formatTime(hoverTime, timeZone);
  };

  const onClickTimeline: React.MouseEventHandler<HTMLDivElement> = (e) => {
    if (timelineRef.current) {
      const rect = timelineRef.current.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const percentage = (x / rect.width) * 100;
      const hoverTime = new Date(
        timelineStartTime + (percentage / 100) * totalDuration
      );
      const hoverTimeISOFormat = hoverTime.toISOString();
      const duration = Math.floor((Date.now() - hoverTime.getTime()) / 1000);

      if (mediaType === 'frames' && duration < 10 * 60) {
        // 10 minutes
        const params = new URLSearchParams();
        params.append('start', hoverTimeISOFormat);
        params.append('duration', String(duration));
        onUpdatePlaybackSrc?.(params.toString());
      } else {
        // eslint-disable-next-line no-console
        console.log('playback not available for time earlier than 10 minutes');
      }
    }
  };

  const getFrameColor = (frame: MediaWithAlarmData) => {
    const alarmLike: Partial<UnifiedAlarm> = {
      id: frame.id,
      severity: frame.severity,
      isDedupEvent: frame.isDedupEvent,
      isPastEvent: frame.isPastEvent,
      resolvedAlarm: frame.resolvedAlarm,
      media: { type: frame.mediaType } as { type: 'video' | 'frames' },
    };
    return alarmColor(alarmLike as UnifiedAlarm);
  };

  const allMediaWithAlarmData = alarms.reduce((acc, alarm) => {
    const frames = alarm.media.frames ?? [];
    const media =
      mediaType === 'frames'
        ? frames
        : [
            {
              id: alarm.id,
              url: alarm.media.video?.url ?? '',
              startTimestamp: alarm.alarmStartTime,
              endTimestamp: alarm.alarmEndTime,
              severity: alarm.severity,
              resolvedAlarm: alarm.resolvedAlarm,
            },
          ];

    const updatedMedia = media.map((frame) => ({
      ...frame,
      id: alarm.id,
      isPastEvent: alarm.isPastEvent,
      isDedupEvent: alarm.isDedupEvent,
      severity: alarm.severity,
      // Frames entries are the alarm's frames, which carry no resolved state;
      // v3 marks minors only through it (see isMinorAlarm).
      resolvedAlarm: alarm.resolvedAlarm,
      mediaType: mediaType,
    }));
    return [...acc, ...updatedMedia];
  }, [] as MediaWithAlarmData[]);

  const getWidth = (frame: MediaWithAlarmData) => {
    if (frame.mediaType === 'frames') {
      return 0.12;
    } else {
      return frame.endTimestamp
        ? ((frame.endTimestamp - (frame.startTimestamp ?? 0)) / totalDuration) *
            100
        : 0;
    }
  };

  return (
    <>
      {hoverImagePosition && hoverFrame && (
        <div
          className="absolute z-20 w-[85%]"
          style={{
            left: `${hoverImagePosition}%`,
            bottom: `${TIMELINE_HEIGHT + HOVER_MARGIN_TOP}px`,
            transform: `translateX(-${getHoverTranslate(
              hoverImagePosition,
              timelineRef
            )})`,
          }}
        >
          <span className="bg-ondark-bg-2 absolute left-1 top-1 flex gap-2 rounded-md px-2 text-xs text-white">
            <span>
              {formatTime(new Date(hoverFrame?.timestamp ?? 0), timeZone)}
            </span>
            {hoverFrame.isPastEvent && (
              <span className="text-[10px]">Past event</span>
            )}
            {hoverFrame.isDedupEvent && (
              <span className="text-[10px]">Dedup event</span>
            )}
          </span>
          {hoverFrame.mediaType === 'frames' && (
            <img
              src={hoverFrame.url}
              alt="event frame"
              className="border-ondark-text-1 rounded-md border"
            />
          )}
          {hoverFrame.mediaType === 'video' && (
            <video
              src={hoverFrame.url}
              autoPlay
              muted
              playsInline
              className="border-ondark-text-1 rounded-md border"
            />
          )}
        </div>
      )}
      {hoverTimePosition && (
        <div
          className="bg-ondark-bg-3/70 absolute cursor-pointer rounded px-2 text-[9px] text-white"
          style={{
            left: `${hoverTimePosition}%`,
            transform: `translateX(-${getHoverTranslate(
              hoverTimePosition,
              timelineRef
            )})`,
            bottom: `${TIMELINE_HEIGHT + HOVER_MARGIN_TOP}px`,
          }}
        >
          {getHoverTime(hoverTimePosition)}
        </div>
      )}
      <div
        ref={timelineRef}
        className={clsx('relative cursor-pointer rounded', 'bg-ondark-bg-1/80')}
        style={{ height: `${TIMELINE_HEIGHT}px` }}
        onMouseMoveCapture={isHoverEnabled ? onHoverTimeline : undefined}
        onMouseOverCapture={isHoverEnabled ? onHoverTimeline : undefined}
        onMouseLeave={onTimelineMouseLeave}
        onClick={onClickTimeline}
      >
        {allMediaWithAlarmData.map((frame, index) => (
          <div
            key={index}
            onMouseOverCapture={onHoverFrame(frame)}
            onMouseLeave={onFrameMouseLeave}
            className={clsx(
              'absolute top-0 h-full rounded-r-md transition-transform',
              getFrameColor(frame)
            )}
            style={{
              left: `${getFramePosition(frame)}%`,
              width: `${getWidth(frame)}%`,
            }}
          ></div>
        ))}

        {hoverTimePosition && (
          <div
            className="bg-primary-500 pointer-events-none absolute z-10 w-[0.12rem]"
            style={{
              left: `${hoverTimePosition}%`,
              height: `${TIMELINE_HEIGHT}px`,
            }}
          ></div>
        )}
      </div>
    </>
  );
}

export default CellMediaTimeline;
